import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { runInAction } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ElementState } from '@src/renderer/ElementState';
import type React from 'react';

const MIN_TIMELINE_SCALE = 0.1;
const PINCH_MULTIPLIER = 1;

interface UseTimelineProps {
  duration: number;
  maxWidth: number;
  frameRate: number;
  enableKeyboardShortcuts?: boolean;
  autoScroll?: boolean;
}

interface PinchState {
  offset: [number, number];
  scale: number;
  velocity: number;
  direction: [number, number];
  maxWidth: number;
  origin: [number, number];
  last: boolean;
}

interface ContextMenuState {
  pos: { left: number; top: number } | null;
  action: 'copy' | 'copied' | 'paste';
  tmp: { element: ElementState };
  copied: any;
  secondaryActions: 'clearVolumeKeyPoints'[];
}

export const useTimeline = ({
  duration,
  maxWidth,
  frameRate,
  enableKeyboardShortcuts = true,
  autoScroll = true,
}: UseTimelineProps) => {
  const videoCreator = useVideoCreatorStore();
  const [isScaling, setIsScaling] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [contextMenuState, setContextMenuState] =
    useState<ContextMenuState | null>(null);

  // Refs for scroll and animation
  const animatedRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const prevTimelineScaleDuration = useRef<number | null>(null);

  // Current values
  const scale = videoCreator.timelineScale;
  const currentTime = videoCreator.time;

  // Auto-scale calculation effect
  const shouldCalcTimelineScale =
    videoCreator.timelineStore.shouldCalcTimelineScale;
  useEffect(() => {
    if (
      duration &&
      duration !== prevTimelineScaleDuration.current &&
      shouldCalcTimelineScale &&
      maxWidth &&
      videoCreator.stateReady
    ) {
      const newScale = (maxWidth - 30) / duration;
      runInAction(() => {
        videoCreator.defaultTimelineScale = newScale;
        videoCreator.timelineScale = newScale * 0.95;
        if (maxWidth !== 700) {
          // FALLBACK_MAX_WIDTH
          videoCreator.timelineStore.setShouldCalcTimelineScale(false);
          prevTimelineScaleDuration.current = duration;
        }
      });
    }
  }, [duration, shouldCalcTimelineScale, maxWidth, videoCreator.stateReady]);

  // Auto-scroll functionality
  const setAnimatedScrollPos = useCallback(
    (scrollFactor = 0.1) => {
      if (animatedRef.current && timelineRef.current && autoScroll) {
        const time = videoCreator.time;
        const offsetX = timelineRef.current.scrollLeft;
        const visibleTimeRange = [
          offsetX / scale,
          (offsetX + maxWidth) / scale,
        ];
        const scrollThreshold = videoCreator.isPlaying
          ? visibleTimeRange[1] -
            0.1 * (visibleTimeRange[1] - visibleTimeRange[0])
          : visibleTimeRange[1];

        if (
          time < visibleTimeRange[0] ||
          (time > scrollThreshold && visibleTimeRange[1] < duration) ||
          scrollFactor === 0.5
        ) {
          const scrollTo =
            Math.max(
              0,
              time - scrollFactor * (visibleTimeRange[1] - visibleTimeRange[0]),
            ) * scale;
          timelineRef.current.scrollLeft = scrollTo;
          animatedRef.current.style.left = `-${timelineRef.current.scrollLeft}px`;
        }
      }
    },
    [videoCreator.isPlaying, maxWidth, duration, scale, autoScroll],
  );

  useEffect(() => {
    if (autoScroll) {
      setAnimatedScrollPos();
    }
  }, [currentTime, setAnimatedScrollPos]);

  useEffect(() => {
    if (autoScroll) {
      setAnimatedScrollPos(0.5);
    }
  }, [scale, setAnimatedScrollPos]);

  // Pinch/zoom handling
  const handlePinch = useCallback(
    (state: PinchState) => {
      setIsScaling(true);
      const { offset } = state;

      const newTimelineScale = Math.max(
        Math.min(
          videoCreator.maxTimelineScale,
          offset[0] * PINCH_MULTIPLIER * videoCreator.defaultTimelineScale,
        ),
        MIN_TIMELINE_SCALE,
      );
      runInAction(() => (videoCreator.timelineScale = newTimelineScale));

      if (timelineRef.current) {
        if (maxWidth - duration * newTimelineScale > 0) {
          if (animatedRef.current) {
            animatedRef.current.style.left = '0px';
          }
          timelineRef.current.scrollLeft = 0;
          return;
        }

        const positionRect = timelineRef.current.getBoundingClientRect();
        const newOffset =
          videoCreator.time * newTimelineScale -
          (positionRect.right - positionRect.left) / 2;
        timelineRef.current.scrollLeft = Math.max(0, newOffset);
      }

      if (state.last) {
        setIsScaling(false);
      }
    },
    [videoCreator, maxWidth, duration],
  );

  // Zoom controls
  const zoomIn = useCallback(() => {
    const newScale = Math.min(
      videoCreator.timelineScale * 1.2,
      videoCreator.maxTimelineScale,
    );
    runInAction(() => (videoCreator.timelineScale = newScale));
  }, [videoCreator]);

  const zoomOut = useCallback(() => {
    const newScale = Math.max(
      videoCreator.timelineScale * 0.8,
      MIN_TIMELINE_SCALE,
    );
    runInAction(() => (videoCreator.timelineScale = newScale));
  }, [videoCreator]);

  const zoomToFit = useCallback(() => {
    if (duration && maxWidth) {
      const newScale = ((maxWidth - 30) / duration) * 0.95;
      runInAction(() => (videoCreator.timelineScale = newScale));
    }
  }, [duration, maxWidth, videoCreator]);

  // Navigation
  const handleTimelineClick = useCallback(
    async (
      e: React.MouseEvent<HTMLDivElement>,
      tracksDivRef: React.RefObject<HTMLDivElement>,
    ) => {
      setIsNavigating(true);
      const isPlaying = videoCreator.isPlaying;
      const offsetX = timelineRef.current?.scrollLeft || 0;

      if (!videoCreator.isPlayheadDragging && tracksDivRef.current) {
        const elementLeftBoundary =
          tracksDivRef.current.getBoundingClientRect().left;
        const clickPositionRelativeToElement = e.clientX - elementLeftBoundary;
        const clickX = clickPositionRelativeToElement + offsetX;

        const time = (clickX - 14) / scale;
        await videoCreator.setTime(time, true);
        if (isPlaying) videoCreator.renderer?.play();
      }
      setIsNavigating(false);
    },
    [videoCreator, scale],
  );

  const navigateToTime = useCallback(
    async (time: number) => {
      setIsNavigating(true);
      await videoCreator.setTime(time, true, true);
      setIsNavigating(false);
    },
    [videoCreator],
  );

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!enableKeyboardShortcuts) return;

      if (e.key === 'ArrowRight') {
        navigateToTime(videoCreator.time + 1 / frameRate);
      } else if (e.key === 'ArrowLeft') {
        navigateToTime(videoCreator.time - 1 / frameRate);
      }
    },
    [videoCreator, frameRate, navigateToTime, enableKeyboardShortcuts],
  );

  const handleKeyUp = useCallback(() => {
    // Handle key up events if needed
  }, []);

  // Context menu handling
  const showContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, element: ElementState) => {
      e.preventDefault();

      const getContextSecondaryActions = (element: ElementState) => {
        let result: 'clearVolumeKeyPoints'[] = [];
        const maybeExtraElementData =
          videoCreator.currentVideo?.extraElementData[element.source.id];
        const hasVolumeKeyPoints =
          maybeExtraElementData !== undefined &&
          maybeExtraElementData !== null &&
          'volumeKeyPoints' in maybeExtraElementData;
        if (
          element?.source?.id &&
          hasVolumeKeyPoints &&
          maybeExtraElementData['volumeKeyPoints']?.length
        ) {
          result.push('clearVolumeKeyPoints');
        }
        return result;
      };

      const contextState: ContextMenuState = {
        pos: { left: e.clientX, top: e.clientY },
        action: 'copy',
        tmp: { element },
        copied: videoCreator.timelineClipboard?.copied || null,
        secondaryActions: getContextSecondaryActions(element),
      };

      setContextMenuState(contextState);
      videoCreator.timelineClipboard = contextState;
    },
    [videoCreator],
  );

  const hideContextMenu = useCallback(() => {
    setContextMenuState(null);
    if (videoCreator.timelineClipboard) {
      videoCreator.timelineClipboard.pos = null;
    }
  }, [videoCreator]);

  const handleContextAction = useCallback(() => {
    // Handle context menu actions
    hideContextMenu();
  }, [hideContextMenu]);

  return {
    // Refs
    animatedRef,
    timelineRef,

    // State
    scale,
    isScaling,
    currentTime,
    isNavigating,
    contextMenuState,

    // Scaling
    handlePinch,
    zoomIn,
    zoomOut,
    zoomToFit,

    // Navigation
    handleTimelineClick,
    navigateToTime,
    handleKeyDown,
    handleKeyUp,

    // Context Menu
    showContextMenu,
    hideContextMenu,
    handleContextAction,
  };
};
